import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, FileText, Package, Clock, Truck, CheckCircle, Printer, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ordersAPI } from '../lib/api';
import { formatPrice, formatDateTime, ORDER_STATUS } from '../lib/utils';
import { printContent, generateOrderDetailHTML } from '../lib/print';
import { getPackageDisplay, getPricePerUnit } from '../lib/packageUtils';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const ORDER_STEPS = [
    { status: 'pending', label: 'En attente', icon: Clock },
    { status: 'confirmed', label: 'Confirmée', icon: CheckCircle },
    { status: 'preparing', label: 'Préparation', icon: Package },
    { status: 'shipped', label: 'Expédiée', icon: Truck },
    { status: 'delivered', label: 'Livrée', icon: CheckCircle },
];

export const OrderDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { isAdmin } = useAuth();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sendingToSuppliers, setSendingToSuppliers] = useState(false);

    useEffect(() => {
        const loadOrder = async () => {
            try {
                const res = await ordersAPI.getById(id);
                setOrder(res.data);
            } catch (error) {
                console.error('Error loading order:', error);
                navigate('/dashboard/commandes');
            } finally {
                setLoading(false);
            }
        };
        loadOrder();
    }, [id, navigate]);

    if (loading) {
        return (
            <div className="min-h-screen bg-muted/50 flex items-center justify-center">
                <div className="animate-pulse text-muted-foreground">Chargement...</div>
            </div>
        );
    }

    if (!order) return null;

    const currentStepIndex = ORDER_STEPS.findIndex(s => s.status === order.status);
    const isCancelled = order.status === 'cancelled';

    const handlePrintOrder = () => {
        const html = generateOrderDetailHTML(order);
        printContent(`Commande ${order.order_number}`, html);
    };

    const handleSendToSuppliers = async () => {
        setSendingToSuppliers(true);
        try {
            const res = await ordersAPI.sendToSuppliers(order.id);
            toast.success(res.data.message);
            // Refresh order
            const updated = await ordersAPI.getById(id);
            setOrder(updated.data);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur envoi aux fournisseurs');
        } finally {
            setSendingToSuppliers(false);
        }
    };

    return (
        <div className="min-h-screen bg-muted/50" data-testid="order-detail-page">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6" data-testid="back-button">
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Retour
                </Button>

                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                    <div>
                        <h1 className="font-heading text-3xl font-bold tracking-tight" data-testid="order-number">
                            Commande {order.order_number}
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Passée le {formatDateTime(order.created_at)}
                            {order.client_code && <span> | Code client : <strong>{order.client_code}</strong></span>}
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        {isAdmin && !order.sent_to_suppliers && (
                            <Button 
                                onClick={handleSendToSuppliers}
                                disabled={sendingToSuppliers}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                data-testid="send-to-suppliers-button"
                            >
                                <Send className="h-4 w-4 mr-2" />
                                {sendingToSuppliers ? 'Envoi...' : 'Envoyer aux fournisseurs'}
                            </Button>
                        )}
                        {order.sent_to_suppliers && (
                            <Badge className="bg-emerald-100 text-emerald-700 px-3 py-1" data-testid="sent-to-suppliers-badge">
                                <Truck className="h-3 w-3 mr-1" /> Envoyée aux fournisseurs
                            </Badge>
                        )}
                        <Button 
                            variant="outline" 
                            onClick={handlePrintOrder}
                            data-testid="print-order-button"
                        >
                            <Printer className="h-4 w-4 mr-2" />
                            Imprimer
                        </Button>
                        <Badge className={`${ORDER_STATUS[order.status]?.color} text-lg px-4 py-2`} data-testid="order-status">
                            {ORDER_STATUS[order.status]?.label}
                        </Badge>
                    </div>
                </div>

                {/* Progress */}
                {!isCancelled && (
                    <Card className="mb-8">
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                                {ORDER_STEPS.map((step, idx) => {
                                    const isCompleted = idx <= currentStepIndex;
                                    const isCurrent = idx === currentStepIndex;
                                    const StepIcon = step.icon;
                                    
                                    return (
                                        <React.Fragment key={step.status}>
                                            <div className="flex flex-col items-center">
                                                <div className={`
                                                    w-10 h-10 rounded-full flex items-center justify-center
                                                    ${isCompleted ? 'bg-leaf text-white' : 'bg-muted text-muted-foreground'}
                                                    ${isCurrent ? 'ring-2 ring-leaf ring-offset-2' : ''}
                                                `}>
                                                    <StepIcon className="h-5 w-5" />
                                                </div>
                                                <span className={`text-xs mt-2 ${isCompleted ? 'font-medium' : 'text-muted-foreground'}`}>
                                                    {step.label}
                                                </span>
                                            </div>
                                            {idx < ORDER_STEPS.length - 1 && (
                                                <div className={`flex-1 h-0.5 mx-2 ${idx < currentStepIndex ? 'bg-leaf' : 'bg-muted'}`} />
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </div>
                        </CardContent>
                    </Card>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Items */}
                    <div className="lg:col-span-2">
                        <Card>
                            <CardHeader>
                                <CardTitle className="font-heading flex items-center gap-2">
                                    <Package className="h-5 w-5 text-leaf" />
                                    Articles commandés
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Produit</TableHead>
                                            <TableHead className="text-right">Qté</TableHead>
                                            <TableHead className="text-right">Prix unit.</TableHead>
                                            <TableHead className="text-right">Sous-total</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {order.items?.map((item, idx) => (
                                            <TableRow key={idx}>
                                                <TableCell className="font-medium">
                                                    {item.product_name}
                                                    {item.package_weight > 0 && (
                                                        <span className="text-xs text-amber-600 ml-1">({getPackageDisplay(item.package_weight, item.unit)}/colis)</span>
                                                    )}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    {item.quantity} colis
                                                    {item.package_weight > 0 && (
                                                        <span className="text-xs text-muted-foreground"> x {getPackageDisplay(item.package_weight, item.unit)}</span>
                                                    )}
                                                </TableCell>
                                                <TableCell className="text-right">{formatPrice(item.price)}{item.package_weight > 0 ? getPricePerUnit(item.unit) : ''}</TableCell>
                                                <TableCell className="text-right font-semibold">{formatPrice(item.subtotal)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>

                                <Separator className="my-4" />

                                <div className="flex justify-between items-center">
                                    <span className="font-heading font-semibold text-lg">Total HT</span>
                                    <span className="font-heading text-2xl font-bold text-leaf" data-testid="order-total">
                                        {formatPrice(order.total)}
                                    </span>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Details */}
                    <div className="space-y-6">
                        <Card>
                            <CardHeader>
                                <CardTitle className="font-heading flex items-center gap-2 text-base">
                                    <MapPin className="h-4 w-4 text-leaf" />
                                    Livraison
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-sm">{order.delivery_address}</p>
                            </CardContent>
                        </Card>

                        {order.notes && (
                            <Card>
                                <CardHeader>
                                    <CardTitle className="font-heading flex items-center gap-2 text-base">
                                        <FileText className="h-4 w-4 text-leaf" />
                                        Notes
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-sm text-muted-foreground">{order.notes}</p>
                                </CardContent>
                            </Card>
                        )}

                        <Card>
                            <CardContent className="pt-6">
                                <div className="text-sm space-y-2">
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Paiement</span>
                                        <span className="font-medium">Facturation</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Dernière MAJ</span>
                                        <span className="font-medium">{formatDateTime(order.updated_at)}</span>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        </div>
    );
};
